/**
 * TIFF decode via utif2 (dynamically imported — only pulled in when a TIFF is
 * actually added). Handles 16-bit → 8-bit reduction through UTIF.toRGBA8, and
 * applies the EXIF orientation tag (274) so output matches the raster path.
 *
 * FALLBACK CHAIN (the ".tif won't decode" fix): when utif2 throws on a TIFF
 * variant it can't parse (e.g. exotic compression, BigTIFF layouts), we log the
 * underlying error and try the browser's native createImageBitmap — Safari
 * decodes TIFF natively. Only when BOTH fail does the error propagate (the
 * caller wraps it in a friendly DecodeError).
 */
import { bitmapFromImageData } from './preview';
import { decodeRaster } from './raster';

type Ifd = { width: number; height: number } & Record<string, unknown>;

function readOrientation(ifd: Ifd): number {
  const raw = ifd['t274'];
  if (Array.isArray(raw) && typeof raw[0] === 'number') return raw[0];
  if (typeof raw === 'number') return raw;
  return 1;
}

/** Redraw RGBA pixels applying an EXIF orientation (1..8) → oriented bitmap. */
async function orientedBitmap(data: ImageData, orientation: number): Promise<ImageBitmap> {
  if (orientation === 1) return bitmapFromImageData(data);
  const base = await bitmapFromImageData(data);
  const swap = orientation >= 5 && orientation <= 8;
  const outW = swap ? data.height : data.width;
  const outH = swap ? data.width : data.height;
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(outW, outH)
      : Object.assign(document.createElement('canvas'), { width: outW, height: outH });
  const ctx = canvas.getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) return base;
  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, outW, 0);
      break;
    case 3:
      ctx.transform(-1, 0, 0, -1, outW, outH);
      break;
    case 4:
      ctx.transform(1, 0, 0, -1, 0, outH);
      break;
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx.transform(0, 1, -1, 0, outW, 0);
      break;
    case 7:
      ctx.transform(0, -1, -1, 0, outW, outH);
      break;
    case 8:
      ctx.transform(0, -1, 1, 0, 0, outH);
      break;
    default:
      break;
  }
  ctx.drawImage(base, 0, 0);
  base.close?.();
  return createImageBitmap(canvas);
}

async function decodeTiffViaUtif(file: Blob): Promise<ImageBitmap> {
  const UTIF = (await import('utif2')).default;
  const buffer = await file.arrayBuffer();
  const ifds = UTIF.decode(buffer) as Ifd[];
  if (!ifds.length) throw new Error('No images in TIFF');
  const ifd = ifds[0]!;
  UTIF.decodeImage(buffer, ifd);
  const rgba = UTIF.toRGBA8(ifd); // Uint8Array RGBA, 8-bit per channel
  if (!ifd.width || !ifd.height || !rgba?.length) throw new Error('Empty TIFF decode result');
  // Copy into a fresh ArrayBuffer-backed clamped array (ImageData requires it).
  const clamped = new Uint8ClampedArray(rgba);
  const imageData = new ImageData(clamped, ifd.width, ifd.height);
  return orientedBitmap(imageData, readOrientation(ifd));
}

export async function decodeTiff(file: Blob): Promise<ImageBitmap> {
  try {
    return await decodeTiffViaUtif(file);
  } catch (utifErr) {
    console.warn('[decode] utif2 failed on this TIFF; trying the native decoder:', utifErr);
    try {
      return await decodeRaster(file); // Safari decodes TIFF natively
    } catch (nativeErr) {
      console.warn('[decode] native TIFF decode also failed:', nativeErr);
      throw utifErr; // the original decoder error is the diagnostic one
    }
  }
}
